<template>
	<v-main>
		<v-container fluid fill-height>
			<v-layout justify-center align-center>
				<v-card width="100%" outlined elevation-0>
					<v-toolbar flat class="border_bottom" shaped>
						<v-toolbar-title>Costi manuali</v-toolbar-title>
						<v-spacer></v-spacer>
						<v-toolbar-items>
							<v-btn text title="Nuovo costo" @click="$router.push('/finance/costs/new')">
								<v-icon>mdi-plus</v-icon>
							</v-btn>
						</v-toolbar-items>
					</v-toolbar>

					<v-col cols="12">
						<v-row>
							<v-col cols="12" md="3">
								<v-text-field
									v-model="searchfield"
									label="Cerca descrizione o fornitore"
									outlined
									dense
									hide-details
									clearable
									append-icon="mdi-magnify"
									@click:append="getSearch()"
									@keyup.enter="getSearch()"
									@click:clear="pulisciRicerca"
								/>
							</v-col>
							<v-col cols="12" md="3">
								<v-select v-model="settings.filters.category" :items="categoryItems" label="Categoria" outlined dense hide-details />
							</v-col>
							<v-col cols="12" md="3">
								<v-text-field v-model="settings.filters.from" type="month" label="Dal mese" outlined dense hide-details clearable />
							</v-col>
							<v-col cols="12" md="3">
								<v-text-field v-model="settings.filters.to" type="month" label="Al mese" outlined dense hide-details clearable />
							</v-col>
						</v-row>
					</v-col>

					<v-data-table
						:headers="headers"
						:items="getCosts"
						:options.sync="settings.options"
						:server-items-length="total"
						must-sort
						class="elevation-0"
						:footer-props="{ 'items-per-page-options': listSize }"
						item-key="_id"
						no-data-text="Nessun costo inserito"
					>
						<template v-slot:[`item.menu`]="{ item }">
							<v-menu offset-y>
								<template v-slot:activator="{ on, attrs }">
									<v-btn icon small v-bind="attrs" title="Azioni" v-on="on"><v-icon small>mdi-dots-vertical</v-icon></v-btn>
								</template>
								<v-list dense>
									<v-list-item link @click="$router.push(`/finance/costs/${item._id}`)">
										<v-list-item-icon><v-icon small>mdi-pencil-outline</v-icon></v-list-item-icon>
										<v-list-item-title>Modifica</v-list-item-title>
									</v-list-item>
									<v-list-item link @click="chiediElimina(item)">
										<v-list-item-icon><v-icon small color="error">mdi-delete-outline</v-icon></v-list-item-icon>
										<v-list-item-title class="error--text">Elimina</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</template>
						<template v-slot:[`item.month`]="{ item }">
							<div class="text-no-wrap">{{ monthLabel(item.month) }}</div>
							<div v-if="item.recurring && item.recurring.monthly" class="fin-muted text-no-wrap">
								<v-icon x-small>mdi-repeat</v-icon> ogni mese{{ item.recurring.until ? ` fino a ${monthLabel(item.recurring.until)}` : '' }}
							</div>
						</template>
						<template v-slot:[`item.description`]="{ item }">
							<div>{{ item.description || '—' }}</div>
							<div v-if="item.counterpart" class="fin-muted">{{ item.counterpart }}</div>
						</template>
						<template v-slot:[`item.net`]="{ item }">{{ formatEurCents(item.net) }}</template>
						<template v-slot:[`item.vat`]="{ item }">
							<div class="text-no-wrap">{{ formatEurCents(item.vat) }}</div>
							<div class="fin-muted text-no-wrap">
								{{ item.vatRate > 0 ? `${item.vatRate}%` : vatNature(item.vatNature) }}{{ item.vatNonDeductiblePct ? ` · ${item.vatNonDeductiblePct}% indetraibile` : '' }}
							</div>
						</template>
						<template v-slot:[`item.gross`]="{ item }">{{ formatEurCents(item.gross) }}</template>
					</v-data-table>
				</v-card>
			</v-layout>
		</v-container>

		<v-dialog v-model="dialogElimina" max-width="440">
			<v-card>
				<v-card-title class="text-h6">Eliminare il costo?</v-card-title>
				<v-card-text>
					{{ daEliminare ? `${daEliminare.category} · ${monthLabel(daEliminare.month)} · ${formatEurCents(daEliminare.gross)}` : '' }}. I mesi già fotografati nel report restano come sono finché non
					li ricalcoli.
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="dialogElimina = false">Annulla</v-btn>
					<v-btn text color="error" @click="elimina">Elimina</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-main>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { listRouteState } from '@/components/core/list/listRouteState';
import { formatEurCents, monthLabel, VAT_NATURES } from '@/components/finance/format';

const LIST_SIZE = [15, 25, 50, 100];

export default {
	name: 'FinanceCosts',
	// pagina, ordinamento, ricerca, categoria e mesi stanno nell'URL
	mixins: [listRouteState({ filters: { category: 'string', from: 'string', to: 'string' }, perPageOptions: LIST_SIZE })],
	data() {
		return {
			settings: this.listSettingsFromRoute({
				search: '',
				filters: { category: '', from: '', to: '' },
				options: { sortBy: ['month'], sortDesc: [true], page: 1, itemsPerPage: 25 },
			}),
			listSize: LIST_SIZE,
			searchfield: '',
			total: 0,
			categories: [],
			dialogElimina: false,
			daEliminare: null,
			headers: [
				{ text: '', value: 'menu', sortable: false, width: 44 },
				{ text: 'Mese', value: 'month' },
				{ text: 'Categoria', value: 'category' },
				{ text: 'Descrizione', value: 'description', sortable: false },
				{ text: 'Imponibile', value: 'net', align: 'end' },
				{ text: 'IVA', value: 'vat', align: 'end', sortable: false },
				{ text: 'Totale', value: 'gross', align: 'end' },
			],
		};
	},
	computed: {
		...mapGetters({ getCosts: 'finance/getCosts' }),
		categoryItems() {
			return [{ value: '', text: 'Tutte' }, ...this.categories.map(c => ({ value: c, text: c }))];
		},
	},
	async mounted() {
		this.searchfield = this.settings.search || '';
		this.listLoad();
		try {
			const res = await this.fetchSettings();
			this.categories = (res.settings && res.settings.categories) || [];
		} catch (err) {
			this.errore(err);
		}
	},
	methods: {
		...mapMutations({ setPreload: 'settings/setPreload', setLogout: 'profile/setLogout' }),
		...mapActions({ fetchCosts: 'finance/fetchCosts', deleteCost: 'finance/deleteCost', fetchSettings: 'finance/fetchSettings' }),
		formatEurCents,
		monthLabel,
		vatNature(value) {
			return (VAT_NATURES.find(n => n.value === value) || { text: '0%' }).text;
		},
		getDataFromApi(preload = true) {
			if (preload) this.setPreload(true);
			return this.fetchCosts(this.settings)
				.then(res => {
					this.total = res.total;
				})
				.catch(err => this.errore(err))
				.finally(() => preload && this.setPreload(false));
		},
		getSearch() {
			this.settings.search = _.size(this.searchfield) >= 3 ? this.searchfield : '';
		},
		pulisciRicerca() {
			this.searchfield = '';
			this.getSearch();
		},
		chiediElimina(item) {
			this.daEliminare = item;
			this.dialogElimina = true;
		},
		async elimina() {
			this.dialogElimina = false;
			try {
				await this.deleteCost(this.daEliminare._id);
				this.notify('success', 'Costo eliminato');
				await this.getDataFromApi(false);
			} catch (err) {
				this.errore(err);
			} finally {
				this.daEliminare = null;
			}
		},
		notify(color, text) {
			this.$store.dispatch('settings/updateSnackbar', { color, text, snackbar: true, mode: 'multi-line', timeout: 5000 });
		},
		errore(err) {
			const res = err.response;
			if (res && res.status === 401) {
				this.setLogout();
				this.$router.push('/login');
				return;
			}
			const errors = res && res.data && res.data.error;
			this.notify('error', Array.isArray(errors) ? errors.map(e => e.msg).join(', ') : (res && res.data && res.data.message) || err.message);
		},
	},
};
</script>

<style scoped>
.fin-muted {
	font-size: 12px;
	color: #5f6b7a;
}
</style>
